import type { Section, SimpleHouse, SimpleProject } from '../../types';

import { estClient } from './estClient';

export interface UnitSegment {
  id: string;
  name: string;
}

/**
 * Kind of "filter" that defines some sample of projects/houses/sections/units
 * which is applied to particular UnitSegment by `unit_segment_id`
 */
export interface UnitSegmentPart {
  id: string;
  unit_segment_id: string;
  object_id: string | null;
  house_id: string | null;
  section_id: string | null;
  floor: string | null;
  unit_type_id: string | null;
  unit_id: string | null;
  rooms: '1' | '2' | '3' | '4' | null;
  area_from: string | null;
  area_to: string | null;
}

/**
 * Separate format that used for creation/update of unit segment
 * Combines UnitSegment and UnitSegmentPart fields
 */
export interface UnitSegmentMutation {
  name: string;
  objects: string[] | null;
  houses: string[] | null;
  sections: string[] | null;
  unit_types: string[] | null;
  units: string[] | null;
  rooms: {
    /**
     * In case when we want to set area for all the rooms this field will be `rooms: null`.
     *
     * If we have item with `rooms: null` this also means that it must the only item in filter selection
     */
    rooms: '1' | '2' | '3' | '4' | null;
    area_from: string | null;
    area_to: string | null;
  }[];
}

/**
 * A list of already available UnitSegments and additional data to allow defining new segments
 */
export interface UnitSegmentListResponse {
  success: true;
  message: 'All went OK';

  /**
   * A list of unit segments. Each segment may relate to one or more filters that
   * define some sample of projects/houses/sections/units
   */
  unit_segment: UnitSegment[];

  /**
   * A list of unit segments specifications that relating to corresponding
   * UnitSegment and specifying filtering params that define some sample of projects/houses/sections/units
   */
  unit_segment_part: UnitSegmentPart[];

  /**
   * Projects list to allow building new segments from
   */
  object: SimpleProject[];

  /**
   * Houses list to allow building new segments from
   */
  house: SimpleHouse[];

  /**
   * Sections list to allow building new segments from
   */
  section: Section[];

  /**
   * The list of available unit types to display them in UI for creating new segment
   */
  unit_type: {
    id: string;
    name: string;

    /**
     * Translation for current company locale
     */
    locale: string;
  }[];
}

export interface UnitSegmentResponse extends UnitSegmentListResponse {
  payload: UnitSegmentMutation;
}

class UnitSegmentApi {
  getAll(): Promise<UnitSegmentListResponse> {
    return estClient.get<UnitSegmentListResponse>('/api/fe/unit_segment');
  }

  getSegment(segmentId: string): Promise<UnitSegmentResponse> {
    return estClient.get<UnitSegmentResponse>(
      `/api/fe/unit_segment/${segmentId}`,
    );
  }

  async createUnitSegment(
    body: UnitSegmentMutation,
  ): Promise<UnitSegmentResponse> {
    const resp = await estClient.post<UnitSegmentResponse>(
      '/api/fe/unit_segment',
      {
        body,
      },
    );

    return resp;
  }

  async updateUnitSegment(
    segmentId: string,
    body: UnitSegmentMutation,
  ): Promise<UnitSegmentResponse> {
    const resp = await estClient.put<UnitSegmentResponse>(
      `/api/fe/unit_segment/${segmentId}`,
      {
        body,
      },
    );

    return resp;
  }
}

export const unitSegmentApi = new UnitSegmentApi();
